import { createRouter, createWebHistory } from "vue-router";
import type { RouteComponent, RouteRecordRaw } from "vue-router";
import { registerGuard } from "@/authentication/authRouterGuard";
import { useUserProfileStore } from "@/modules/userProfile/store/userProfileStore";
import { toRaw } from "vue";
import { getUIRole } from "@/modules/userProfile/types/UserProfile";
import { useNavigationStore } from "@/modules/navigation/store/navigationStore";
import { getEnvConfig } from "@/config/envConfig";

let isFirstNavigation = true;
let userRoles: string;
const envConfig = getEnvConfig();
const isFeatureEnabled = (flag: string | boolean | null | undefined): boolean =>
  String(flag).trim().toLowerCase() === "true";
const isServiceCatalogueManagerEnabled = isFeatureEnabled(envConfig.showServiceCatalogueManagerV1FeatureFlag);
const isServiceCatalogueLandingEnabled = isFeatureEnabled(envConfig.showServiceCatalogueLandingFeatureFlag);

async function detectUserRoles() {
  const userProfileStore = useUserProfileStore();
  const userProfile = await userProfileStore.fetchUserProfile();
  if (!userProfile?.roles) {
    return ["LFD User"];
  }
  const profileRole = toRaw(userProfile?.roles);
  userRoles = getUIRole(profileRole);
  return userRoles;
}

function adminOnlyGuard(to: any, from: any, next: any) {
  if (userRoles === "Admin") {
    next();
  } else {
    next({ name: "AccessDenied" });
  }
  console.debug("from", from, "to", to, "next", next);
}

function adminWithFlagGuard(flag: boolean) {
  return (to: any, from: any, next: any) => {
    if (userRoles === "Admin" && flag) {
      next();
    } else {
      next({ name: "AccessDenied" });
    }
    console.debug("from", from, "to", to, "next", next);
  };
}

export const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "Home",
    component: (): Promise<RouteComponent> => import("@/views/HomeView.vue"),
    props: true,
    meta: { requiresAuth: true, breadcrumb: "Employee Center" },
    beforeEnter: (to, from, next) => {
      const routeMap = {
        Practitioner: "Practitioner",
        AdminHubView: "AdminHubView",
        RequestDetails: "Home",
        CatalogueDetailView: "Home",
        LegalRequestForm: "Home",
        ServiceCatalogueManager: "Home",
        ServiceCatalogueLanding: "Home",
        TeamManager: "Home",
        ViewAllUserRequests: "Home"
      };

      if (to.name === "Home" && routeMap[from.name]) {
        next();
      } else if (from.name === "Home" && routeMap[to.name] && userRoles === to.name) {
        next({ name: to.name });
      } else if (userRoles === "Admin") {
        next({ name: "AdminHubView" });
      } else if (userRoles === "Concierge" || userRoles === "Practitioner") {
        next({ name: "Practitioner" });
      } else {
        next();
      }
    }
  },
  {
    path: "/catalogue-details/:id",
    name: "CatalogueDetailView",
    component: (): Promise<RouteComponent> => import("@/components/CatalogueDetailView.vue"),
    props: true,
    meta: { requiresAuth: true, breadcrumb: "Catalogue Details" },
    beforeEnter: (to, from, next) => {
      const catalogueId = Number(to.params.id);
      if (Number.isFinite(catalogueId) && catalogueId > 0) {
        next();
      } else {
        next({ name: "AccessDenied" });
      }
      console.debug("from", from, "to", to, "next", next);
    }
  },
  {
    path: "/legal-advice/form-assignment/:assignmentId",
    name: "LegalRequestFormDeepLink",
    component: (): Promise<RouteComponent> => import("@/components/LegalRequestForm.vue"),
    props: (route) => ({
      assignmentId: Number(route.params.assignmentId)
    }),
    meta: { requiresAuth: true, breadcrumb: "Legal Request Form" },
    beforeEnter: (to, _from, next) => {
      const assignmentId = Number(to.params.assignmentId);
      if (Number.isFinite(assignmentId) && assignmentId > 0) {
        next();
      } else {
        next({ name: "AccessDenied" });
      }
    }
  },
  {
    path: "/access-denied",
    name: "AccessDenied",
    component: (): Promise<RouteComponent> => import("@/views/AccessDeniedView.vue"),
    props: true,
    meta: { requiresAuth: false }
  },
  {
    path: "/legal-advice",
    name: "LegalRequestForm",
    component: (): Promise<RouteComponent> => import("@/components/LegalRequestForm.vue"),
    props: true,
    meta: { requiresAuth: true, breadcrumb: "Legal Request Form" },
    beforeEnter: (to, from, next) => {
      if (from.name === "Home" || from.name === "ViewAllUserRequests" || from.name === "RequestDetails") {
        next();
      } else {
        next({ name: "AccessDenied" });
      }
      console.debug("from", from, "to", to, "next", next);
    }
  },
  {
    path: "/request-details/:id",
    name: "RequestDetails",
    component: (): Promise<RouteComponent> => import("@/components/RequestDetails.vue"),
    props: true,
    meta: { requiresAuth: true, breadcrumb: "Request Details" },
    beforeEnter: async (to, from, next) => {
      if (
        from.name === "LegalRequestForm" ||
        from.name === "ViewAllUserRequests" ||
        to.query.fromComponent === "emailNotification" ||
        to.query.fromComponent === "UserRequest"
      ) {
        next();
      } else {
        next({ name: "AccessDenied" });
      }
    }
  },
  {
    path: "/view-all-requests",
    name: "ViewAllUserRequests",
    component: (): Promise<RouteComponent> => import("@/components/ViewAllUserRequests.vue"),
    props: true,
    meta: { requiresAuth: true, breadcrumb: "View All Requests" },
    beforeEnter: (to, from, next) => {
      if (
        from.name === "Home" ||
        from.name === "LegalRequestForm" ||
        from.name === "RequestDetails" ||
        from.name === "LegalRequestFormDeepLink"
      ) {
        next();
      } else {
        next({ name: "AccessDenied" });
      }
      console.debug("from", from, "to", to, "next", next);
    }
  },
  {
    path: "/practitioner",
    name: "Practitioner",
    component: (): Promise<RouteComponent> => import("@/views/PractitionerLandingView.vue"),
    props: true,
    meta: { requiresAuth: true, breadcrumb: "Practitioner Workbench" },
    beforeEnter: (to, from, next) => {
      if (userRoles === "Admin" || userRoles === "Concierge" || userRoles === "Practitioner") {
        next();
      } else {
        next({ name: "AccessDenied" });
      }
      console.debug("from", from, "to", to, "next", next);
    }
  },
  {
    path: "/admin-hub",
    name: "AdminHubView",
    component: (): Promise<RouteComponent> => import("@/views/AdminHubView.vue"),
    props: true,
    meta: { requiresAuth: true, breadcrumb: "Admin Hub" },
    beforeEnter: adminOnlyGuard
  },
  {
    path: "/service-catalogue-manager",
    name: "ServiceCatalogueManager",
    component: (): Promise<RouteComponent> => import("@/components/adminHub/ServiceCatalogueManagerV1.vue"),
    props: true,
    meta: { requiresAuth: true, breadcrumb: "Service Catalogue Manager" },
    beforeEnter: adminWithFlagGuard(isServiceCatalogueManagerEnabled)
  },
  {
    path: "/service-catalogue-management",
    name: "ServiceCatalogueLanding",
    component: (): Promise<RouteComponent> => import("@/components/adminHub/ServiceCatalogueLanding.vue"),
    props: true,
    meta: { requiresAuth: true, breadcrumb: "Service Catalogue Manager" },
    beforeEnter: adminWithFlagGuard(isServiceCatalogueLandingEnabled)
  },
  {
    path: "/team-manager",
    name: "TeamManager",
    component: (): Promise<RouteComponent> => import("@/components/adminHub/TeamManager.vue"),
    props: true,
    meta: { requiresAuth: true, breadcrumb: "Team Manager" },
    beforeEnter: adminOnlyGuard
  },
  {
    path: "/form-manager",
    name: "FormManager",
    component: (): Promise<RouteComponent> => import("@/components/adminHub/FormManager.vue"),
    props: true,
    meta: { requiresAuth: true, breadcrumb: "Form Manager" },
    beforeEnter: adminOnlyGuard
  },
  //  If no match found for route then redirect to home page
  {
    path: "/:pathMatch(.*)*",
    redirect: { name: "Home" }
  }
];

const router = createRouter({
  history: createWebHistory("/"),
  // See https://router.vuejs.org/guide/advanced/scroll-behavior.html
  // Provides smooth scrolling back-to-top behaviour on page transition
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      console.debug("scrolling to top", to, from, savedPosition);
      return { left: 0, top: 0, behaviour: "smooth" };
    }
  },
  routes
});

router.beforeEach(async (to, from, next) => {
  const userRoles = await detectUserRoles();
  if (isFirstNavigation && userRoles === "Practitioner") {
    isFirstNavigation = false;
    next();
  } else {
    const navigationStore = useNavigationStore();
    navigationStore.setTraversePath(to, from);
    next();
  }
});

registerGuard(router);

/** After a deploy, hashed chunks may 404 while the tab still runs an older bundle; recover with one full reload. */
const CHUNK_RECOVERY_KEY = "lfd_chunk_recovery";

router.afterEach(() => {
  sessionStorage.removeItem(CHUNK_RECOVERY_KEY);
});

router.onError((error) => {
  const message = error instanceof Error ? error.message : String(error);
  const chunkFailure =
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    message.includes("Unable to preload CSS");

  if (!chunkFailure) {
    return;
  }

  if (sessionStorage.getItem(CHUNK_RECOVERY_KEY)) {
    console.error("Asset load failed after recovery reload:", error);
    sessionStorage.removeItem(CHUNK_RECOVERY_KEY);
    return;
  }

  sessionStorage.setItem(CHUNK_RECOVERY_KEY, "1");
  globalThis.location.reload();
});

export default router;
